import { atom } from "jotai"
import {
	currentPhotoAtom,
	displayedPhotosAtom,
	getCullingInfo,
	photosAtom,
	selectedPhotosAtom,
} from "../photo/photos-state"
import { store } from "../store"
import { visibleThumbnailsAtom } from "../previews/previews-state"
import { projectAtom } from "../project/projects-state"
import { PhotoId } from "../undoable-state"
import { getImageInfo } from "./image-info"
import { ClusterThreshold, currentClusterAtom } from "../importing/culling-state"
import { currentStyleAtom } from "../style/style-state"

export const photosProcessingPriorityAtom = atom((get) => {
	const currentPhoto = get(currentPhotoAtom)
	const selectedPhotos = get(selectedPhotosAtom)
	const displayedPhotos = get(displayedPhotosAtom)
	const visibleThumbs = get(visibleThumbnailsAtom)
	const currentCluster = get(currentClusterAtom)
	const currentStyle = get(currentStyleAtom)
	return {
		currentPhoto,
		selectedPhotos,
		displayedPhotos,
		visibleThumbs,
		currentCluster,
		currentStyle,
	}
})

export const getVisibleThumbsProcessingPriorities = () => {
	const {
		currentPhoto,
		selectedPhotos,
		displayedPhotos,
		visibleThumbs,
		currentCluster,
		// currentStyle,
	} = store.get(photosProcessingPriorityAtom)

	const mergedVisibleThumbs = currentCluster
		? new Set([...visibleThumbs, ...currentCluster.clusterPhotoIds])
		: visibleThumbs

	const photos = store.get(photosAtom)
	const project = store.get(projectAtom)

	const priorities = new Map<PhotoId, number>()
	if (!project) return priorities

	/**
	 * Set priority based on order
	 * Visible files have a higher priority
	 * Files after visible area should also have a higher priority so that
	 * processing should continue after visible area
	 */

	let lastVisibleIndex = 0

	displayedPhotos.forEach((photo, index) => {
		const key = photo.id

		const isVisible = mergedVisibleThumbs.has(key)
		lastVisibleIndex = isVisible ? index : lastVisibleIndex

		const normalPriority = lastVisibleIndex && index > lastVisibleIndex ? 100_000 : 0
		const visiblePriority = isVisible ? normalPriority + 100_000 : normalPriority

		const order = displayedPhotos.length - index
		const priority = visiblePriority + order

		priorities.set(key, priority)
	})

	/**
	 *  Selected files have an even higher priority
	 */

	selectedPhotos.forEach((photo) => {
		const key = photo.id
		const priority = priorities.get(key) ?? 0
		priorities.set(key, priority + 100_000)
	})

	/**
	 * The currently loaded file should have the highest priority
	 */

	if (currentPhoto) {
		const key = currentPhoto.id
		const priority = priorities.get(key) ?? 0
		priorities.set(key, priority + 1_000_000)
	}

	// We assure the prioritzed images are already key photos in pre-clustering, so no need to bump priority here
	// if (currentStyle?.prioritizeImageIds) {
	// 	currentStyle.prioritizeImageIds.forEach((id) => {
	// 		const priority = priorities.get(id) ?? 0
	// 		priorities.set(id, priority + 10_000_000)
	// 	})
	// }

	/**
	 * Ensure that the key photo in each cluster is processed first.
	 *This is necessary for AI processing to be correct.
	 */

	for (const photoId in photos) {
		const imageInfo = getImageInfo(photoId)
		let keyPhotoID = imageInfo?.keyNeighborId
		if (!keyPhotoID) {
			const cullingInfo = getCullingInfo(photoId)
			keyPhotoID = cullingInfo?.clusterLeaderIdMap?.[ClusterThreshold.EditClustering]
			const keyImgeInfo = getImageInfo(keyPhotoID ?? "")
			if (keyImgeInfo && keyImgeInfo.keyNeighborId) {
				// This is the true key photo ID, in case the key photo has been changed since pre-clustering
				keyPhotoID = keyImgeInfo.keyNeighborId
			}
		}
		if (keyPhotoID && keyPhotoID !== photoId) {
			const keyPriority = priorities.get(keyPhotoID) ?? 0
			const priority = Math.max(keyPriority, (priorities.get(photoId) ?? 0) + 1)
			priorities.set(keyPhotoID, priority)
		}
	}

	return priorities
}
